// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import addProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/AddProductFilters";
import {
    getProductListDataViewProperty,
    isLoadMore,
} from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Pagination, { PaginationPresentationProps } from "@insite/mobius/Pagination";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

// <XNG-Changes>
const enum fields {
    showResultsPerPage = "showResultsPerPage",
    showPagesNumber = "showPagesNumber",
    alignX = "alignX",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showResultsPerPage]: boolean;
        [fields.showPagesNumber]: boolean;
        [fields.alignX]: string;
    };
}
// </XNG-Changes>

const mapStateToProps = (state: ApplicationState) => ({
    hidePagination: isLoadMore(state),
    pagination: getProductListDataViewProperty(state, "pagination"),
});

const mapDispatchToProps = {
    addProductFilters,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductListPaginationStyles {
    pagination?: PaginationPresentationProps;
}

export const paginationStyles: ProductListPaginationStyles = {};

const styles = paginationStyles;

const ProductListPagination = ({
    addProductFilters,
    hidePagination,
    pagination,
    // XNG-Changes
    fields: { showResultsPerPage, showPagesNumber, alignX },
}: Props) => {
    const [shouldScrollToTop, setShouldScrollToTop] = useState(false);
    useEffect(() => {
        if (shouldScrollToTop) {
            window.scrollTo(0, 0);
            setShouldScrollToTop(false);
        }
    }, [pagination?.page]);

    const changePage = (newPageIndex: number) => {
        addProductFilters({ page: newPageIndex });
        setShouldScrollToTop(true);
    };

    const changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPageSize = parseInt(event.currentTarget.value, 10);
        addProductFilters({ pageSize: newPageSize });
    };

    if (hidePagination || !pagination || !pagination.totalItemCount) {
        return null;
    }

    return (
        <Pagination
            {...styles.pagination}
            resultsCount={pagination.totalItemCount}
            currentPage={pagination.page}
            resultsPerPage={pagination.pageSize}
            resultsPerPageOptions={pagination.pageSizeOptions}
            onChangePage={changePage}
            onChangeResultsPerPage={changeResultsPerPage}
            pageSizeCookie="ProductList-PageSize"
            data-test-selector="productList_pagination"
            prevPage={pagination.prevPageUri}
            nextPage={pagination.nextPageUri}
            /* XNG-Change */
            {...{ justifyContent: alignX, showResultsPerPage, showPagesNumber }}
        />
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ProductListPagination),
    definition: {
        group: "Product List",
        displayName: "Pagination",
        allowedContexts: ["ProductListPage"],
        // XNG-Changes
        fieldDefinitions: [
            {
                name: fields.showResultsPerPage,
                displayName: "Show Results Per Page",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.showPagesNumber,
                displayName: "Show Pages Number",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.alignX,
                editorTemplate: "RadioButtonsField",
                displayName: "Horizontal Alignment",
                options: [
                    {
                        displayName: "Start",
                        value: "flex-start",
                    },
                    {
                        displayName: "Middle",
                        value: "center",
                    },
                    {
                        displayName: "End",
                        value: "flex-end",
                    },
                ],
                defaultValue: "flex-start",
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
